import { createStore } from '@stencil/store';

import wcShared, { poll, WcNotificationPresetConfig } from '@web-components/shared';
import { parsePhoneNumber } from 'libphonenumber-js';

import { type Challenge } from './constants';
import { ChallengeErrorResponse, ChallengeSuccessResponse, shieldSdk } from './sdk';

export interface ResolveType {
  uuid: null | string;
  challenge: null | Challenge;
}

export const store = (() => {
  const _ = createStore({
    error: undefined as undefined | string,
    loading: undefined as undefined | boolean,

    /** Challenge uuid, and it's associated challenge */
    uuid: '',
    uuidChallenge: null as null | Challenge,

    /** Currently view challenge and avalaible challenges */
    challenge: null as null | Challenge,
    challenges: undefined as undefined | string, // Comma separated values

    /** Carry the promise and resolve fn */
    resolver: undefined as
      | undefined
      | {
          promise: Promise<ResolveType>;
          resolve: (value: ResolveType) => void;
          reject: (reason?: unknown) => void;
        },

    /** Phone call */
    incomingCall: undefined as
      | undefined
      | Partial<{
          expiresAt: string; // An ISO string date
          phoneNumberByCountryCode?: { [k: string]: string };
        }>,

    /** SMS */
    smsCheckLoading: undefined as undefined | boolean,
    sms: undefined as
      | undefined
      | Partial<{
          expiresAt: string; // An ISO string date
          pattern?: string;
          phoneNumber: string;
        }>,

    /** Rate limiting */
    retryAt: undefined as undefined | string, // An ISO string date

    /** Should the SDK errors be displayed in a notification */
    errorNotifications: true,
    errorNotificationSettings: {} as Omit<WcNotificationPresetConfig, 'message'>,
  });

  /**
   * Extract the challenge specific data from a ChallengeSuccessResponse
   *
   * @returns if the challenge is valid
   */
  const updateChallengeData = (data: ChallengeSuccessResponse['data']) => {
    const { current_challenge: challenge, uuid } = data;

    /**
     * If no challenge or no uuid is returned
     *   do not trigger any verification and resolve immediately
     */
    if (!uuid || !challenge) {
      resolve({ uuid, challenge });
      return false;
    }

    // Add uuid and current challenge
    _.set('uuid', uuid);
    _.set('uuidChallenge', challenge);

    // Add challenge-specific data
    // - incoming_call
    if (challenge === 'incoming_call') {
      const {
        data: {
          phones: { match: userPhone, others: phoneNumberByCountryCode },
        },
        expire_at: expiresAt,
      } = data;

      // Parse the country code
      const userPhoneCountryCode = (() => {
        try {
          return parsePhoneNumber(userPhone).country;
        } catch (_) {
          // Noop...
        }
        return 'CH';
      })();

      _.set('incomingCall', {
        expiresAt,
        phoneNumberByCountryCode: Object.entries(phoneNumberByCountryCode).reduce(
          (obj, [key, value]) => {
            obj[key.toUpperCase()] = value;
            return obj;
          },
          { [userPhoneCountryCode as string]: userPhone } as { [key: string]: string },
        ),
      });
      // - sms
    } else if (challenge === 'sms') {
      const {
        data: { phone: phoneNumber },
        expire_at: expiresAt,
        pattern,
      } = data;

      _.set('sms', { expiresAt, pattern, phoneNumber });
    }

    return true;
  };

  /** Update the store with the rate limit data */
  const updateChallengeError = (error: ChallengeErrorResponse['error']) => {
    /** [429] Handle rate-limited */
    if (error.code === 'too_many_request' && typeof error.context?.retry_after === 'number') {
      const d = new Date();
      d.setSeconds(d.getSeconds() + error?.context?.retry_after);
      _.set('retryAt', d.toISOString());
    } else {
      /** Toasted error */
      const message = (() => {
        if (error.code === 'invalid_user_password') {
          return wcShared.translate('Mot de passe utilisateur invalide');
        }

        /** Unhandled error */
        console.error(new Error('Unhandled ik-shield error', { cause: error }));
        return wcShared.translate('Une erreur est survenue');
      })();
      const payload = { message, ..._.get('errorNotificationSettings') };

      wcShared?.notificationService?.showError(payload);
      reject(error);
    }
  };

  /** Initialize a challenge */
  const init = async (arg: string | Parameters<(typeof shieldSdk)['createChallenge']>[0]) => {
    _.set('loading', true);

    const response = await (typeof arg === 'string'
      ? shieldSdk.getChallenge(arg)
      : shieldSdk.createChallenge(arg));
    const { result } = response;
    if (result === 'success') {
      const { data } = response;
      if (!updateChallengeData(response.data)) return;

      // Update current challenge and list of available challenges
      const challenge = data.current_challenge;
      const challenges = [data.current_challenge, ...data.allowed_challenges];
      _.set('challenge', challenge);
      _.set('challenges', (challenges.filter(Boolean) as string[]).join(','));

      // Update state
      _.set('loading', false);
    } else {
      updateChallengeError(response.error);
    }
  };

  /** Change the challenge */
  _.onChange('challenge', async (challenge) => {
    /** Only perfom the query if not selectic the same challenge */
    if (typeof challenge === 'string' && _.get('uuidChallenge') !== challenge) {
      performChallenge(challenge);
    }
  });

  /** Triggers a new challenge, fetching props from API */
  const performChallenge = async (challenge: Challenge) => {
    _.set('loading', true);

    const uuid = _.get('uuid');
    const response = await shieldSdk.putChallenge(uuid, challenge);
    const { result } = response;
    if (result === 'success') {
      if (!updateChallengeData(response.data)) return;

      // Clear loading feedback
      _.set('loading', false);
    } else {
      updateChallengeError(response.error);
    }
  };

  /** Retry the current challenge */
  const retryChallenge = async () => {
    _.set('loading', true);

    const uuid = _.get('uuid');
    const response = await shieldSdk.patchChallenge(uuid);
    const { result } = response;
    if (result === 'success') {
      if (!updateChallengeData(response.data)) return;

      // Clear loading feedback
      _.set('loading', false);
    } else {
      updateChallengeError(response.error);
    }
  };

  /** Resolve with payload, then reset state */
  const resolve = (payload: ResolveType) => {
    const resolver = _.get('resolver');
    if (resolver) {
      resolver.resolve(payload);
      _.reset();
    }
  };

  /** Reject with payload, then reset state */
  const reject = (payload: unknown) => {
    const resolver = _.get('resolver');
    if (resolver) {
      resolver.reject(payload);
      _.reset();
    }
  };

  /** Resolve with the current challenge */
  const resolveChallenge = () => {
    resolve({
      uuid: _.get('uuid'),
      challenge: _.get('challenge'),
    });
  };

  return Object.assign(_, {
    /** Check an SMS OTP */
    checkSmsOtp: async (otp: string) => {
      _.set('smsCheckLoading', true);

      const uuid = _.get('uuid');
      const response = await shieldSdk.postChallenge(uuid, { code: otp });

      _.set('smsCheckLoading', false);

      if (response.result === 'success') {
        return response.data;
      }

      // Rate-limited error
      const { error } = response;
      updateChallengeError(error);
      return false;
    },

    /** Poll for challenge status */
    pollStatus: (() => {
      /** Check if the challenge has been validated */
      const checkStatus = async () => {
        const uuid = _.get('uuid');
        const response = await shieldSdk.isChallengeSolved(uuid);
        return response.result === 'success' && response.data;
      };

      /** Prevent creating multiple concurent polls */
      let pending: ReturnType<typeof poll> | null = null;
      const clean = () => (pending = null);
      return (rate: number) => {
        if (pending) return pending;

        pending = poll(checkStatus, rate);
        pending.then(clean);
        pending.onCancel = clean;
        return pending;
      };
    })(),

    /** Initialize a challenge */
    init,
    /** Triggers a new challenge, fetching props from API */
    performChallenge,
    /** Reject */
    reject,
    /** Resolve the current challenge */
    resolveChallenge,
    /** Retry the current challenge */
    retryChallenge,
  });
})();
